/**
 * RolesTab Component
 *
 * Role emulation panel for testing different permission levels
 * without changing the actual user role.
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
    FiUserCheck,
    FiInfo,
    FiCheck,
    FiX
} from 'react-icons/fi';

const RolesTab = ({
    roles,
    currentRoleId,
    emulatingRole,
    getUser,
    onEmulateRole,
    onStopEmulating
}) => {
    return (
        <div className="dos-settings-section">
            <div className="dos-section-header">
                <FiUserCheck className="dos-section-icon" />
                <span className="dos-section-title">Role Emulation</span>
            </div>

            {emulatingRole && (
                <div className="dos-emulation-banner">
                    <div className="dos-emulation-info">
                        <FiInfo style={{ marginRight: '8px' }} />
                        <span>
                            Currently emulating: <strong>{roles.find(r => r.id === currentRoleId)?.name || 'Unknown'}</strong>
                        </span>
                    </div>
                    <button
                        className="dos-test-button dos-danger"
                        onClick={onStopEmulating}
                    >
                        <FiX style={{ marginRight: '4px' }} />
                        Stop Emulation
                    </button>
                </div>
            )}

            <div className="dos-role-grid">
                {roles.length === 0 ? (
                    <div className="dos-no-roles">
                        <FiUserCheck style={{ fontSize: '32px', opacity: 0.3, marginBottom: '8px' }} />
                        <p>No roles available</p>
                    </div>
                ) : (
                    roles.map(role => {
                        const isCurrentRole = role.id === currentRoleId;
                        const user = getUser();
                        const isActualRole = user && user.role_id === role.id;

                        return (
                            <div
                                key={role.id}
                                className={`dos-role-card ${isCurrentRole ? 'dos-role-active' : ''}`}
                            >
                                <div className="dos-role-header">
                                    <FiUserCheck className="dos-role-icon" />
                                    {isActualRole && (
                                        <div className="dos-role-badge dos-actual-badge">ACTUAL</div>
                                    )}
                                    {isCurrentRole && !isActualRole && (
                                        <div className="dos-role-badge dos-emulated-badge">EMULATED</div>
                                    )}
                                </div>
                                <div className="dos-role-content">
                                    <h4 className="dos-role-name">{role.name}</h4>
                                    {role.description && (
                                        <p className="dos-role-description">{role.description}</p>
                                    )}
                                </div>
                                {!isCurrentRole && (
                                    <button
                                        className="dos-emulate-button"
                                        onClick={() => onEmulateRole(role.id)}
                                    >
                                        <FiUserCheck style={{ marginRight: '4px' }} />
                                        Emulate
                                    </button>
                                )}
                                {isCurrentRole && isActualRole && (
                                    <div className="dos-current-role-indicator">
                                        <FiCheck style={{ marginRight: '4px' }} />
                                        Your Role
                                    </div>
                                )}
                                {isCurrentRole && !isActualRole && (
                                    <div className="dos-current-role-indicator dos-emulating-indicator">
                                        <FiCheck style={{ marginRight: '4px' }} />
                                        Active
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            <div className="dos-test-card dos-test-card-full" style={{ marginTop: '16px' }}>
                <div className="dos-test-card-content">
                    <h4 className="dos-test-title">Role Emulation Info</h4>
                    <p className="dos-test-description">
                        <strong>Purpose:</strong> Test different permission levels without changing your actual user role.<br/><br/>
                        <strong>How it works:</strong> When you emulate a role, your permissions will be checked against that role's settings. The page will reload to apply the changes.<br/><br/>
                        <strong>Stop emulating:</strong> Click "Stop Emulation" to return to your actual role. The page will reload automatically.
                    </p>
                </div>
            </div>
        </div>
    );
};

RolesTab.propTypes = {
    roles: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        description: PropTypes.string
    })).isRequired,
    currentRoleId: PropTypes.number,
    emulatingRole: PropTypes.bool.isRequired,
    getUser: PropTypes.func.isRequired,
    onEmulateRole: PropTypes.func.isRequired,
    onStopEmulating: PropTypes.func.isRequired
};

export default RolesTab;
